<script setup>
// Dropdown custom (não-nativo) com a identidade visual do Chatwoot: trigger no
// estilo do components-next/select/Select.vue e lista flutuante no estilo do
// dropdown-menu/base/DropdownBody.vue. Substitui o <select> nativo (cuja lista
// aberta é renderizada pelo SO e não segue o tema). v-model + evento `change`.
//
// A lista aberta é teleportada para o <body> com posição fixa calculada a partir
// do trigger. Isso faz o menu escapar de qualquer container que corte o overflow
// (ex.: o painel de config estreito dos nós de flow) e crescer o quanto precisar
// para caber os itens (sem truncar nem scroll lateral), reposicionando-se para
// não sair da viewport.
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: opts =>
      opts.every(o => typeof o === 'object' && 'value' in o && 'label' in o),
  },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  // alinhamento vertical do menu: 'bottom' (padrão) abre pra baixo, 'top' pra cima
  align: { type: String, default: 'bottom' },
  // inline: em vez de teleportar/flutuar por cima, abre a lista NO FLUXO (empurra o
  // conteúdo de baixo). Usado onde o container rola (ex.: caixa Kanban da conversa),
  // pra o menu não "sair do box" cobrindo as seções vizinhas. O modo teleport (padrão)
  // continua p/ containers que cortam overflow (painel estreito dos nós de flow).
  inline: { type: Boolean, default: false },
});
const emit = defineEmits(['change']);

const modelValue = defineModel({
  type: [String, Number, Boolean],
  default: '',
});

const isOpen = ref(false);
const triggerRef = ref(null);
const menuRef = ref(null);
const menuStyle = ref({});

const sameValue = (a, b) => String(a) === String(b);
const selected = computed(() =>
  props.options.find(o => sameValue(o.value, modelValue.value))
);
const triggerLabel = computed(() => selected.value?.label ?? props.placeholder);

const positionMenu = () => {
  if (props.inline) return; // no fluxo: sem posicionamento fixo
  const el = triggerRef.value;
  const menu = menuRef.value;
  if (!el || !menu) return;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 4;
  const pad = 8;
  const mw = menu.offsetWidth;
  const mh = menu.offsetHeight;

  // horizontal: alinha pela esquerda do trigger; se vazar à direita, encosta à
  // direita; nunca deixa passar da borda esquerda da viewport.
  let left = r.left;
  if (left + mw > vw - pad) left = r.right - mw;
  if (left < pad) left = pad;

  // vertical: abre pra baixo; se não couber e couber pra cima, abre pra cima.
  const wantUp = props.align === 'top';
  let top = r.bottom + gap;
  const fitsBelow = r.bottom + gap + mh <= vh - pad;
  const fitsAbove = r.top - gap - mh >= pad;
  if ((wantUp && fitsAbove) || (!fitsBelow && fitsAbove)) {
    top = r.top - gap - mh;
  }

  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
};

const reposition = () => {
  if (isOpen.value) positionMenu();
};

const onDocPointer = e => {
  if (!isOpen.value) return;
  const t = e.target;
  if (triggerRef.value?.contains(t) || menuRef.value?.contains(t)) return;
  // ciclo intencional: close() chama unbindGlobal(), que remove este listener
  // eslint-disable-next-line no-use-before-define
  close();
};

const bindGlobal = () => {
  window.addEventListener('scroll', reposition, true);
  window.addEventListener('resize', reposition);
  document.addEventListener('mousedown', onDocPointer, true);
};
const unbindGlobal = () => {
  window.removeEventListener('scroll', reposition, true);
  window.removeEventListener('resize', reposition);
  document.removeEventListener('mousedown', onDocPointer, true);
};

const close = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  unbindGlobal();
};

const open = async () => {
  if (props.inline) {
    // no fluxo: sem style fixo; a lista ocupa a largura do trigger e empurra o resto.
    menuStyle.value = {};
    isOpen.value = true;
    bindGlobal(); // mantém o fechar-ao-clicar-fora
    await nextTick();
    // como pode abrir com o box no rodapé, rola a lista pra dentro da vista.
    menuRef.value?.scrollIntoView({ block: 'nearest' });
    return;
  }
  const r = triggerRef.value?.getBoundingClientRect();
  // largura mínima = largura do trigger; cresce até caber os itens, com teto.
  menuStyle.value = {
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    minWidth: r ? `${Math.round(r.width)}px` : undefined,
    maxWidth: 'min(20rem, 90vw)',
    zIndex: 9999,
  };
  isOpen.value = true;
  bindGlobal();
  await nextTick();
  positionMenu();
};

const toggle = () => {
  if (props.disabled) return;
  if (isOpen.value) close();
  else open();
};

const choose = option => {
  if (option.disabled) return;
  const changed = !sameValue(option.value, modelValue.value);
  modelValue.value = option.value;
  close();
  if (changed) emit('change', option.value);
};

onBeforeUnmount(unbindGlobal);
</script>

<template>
  <div class="relative w-full">
    <button
      ref="triggerRef"
      type="button"
      :disabled="disabled"
      class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm text-left transition-all duration-200 rounded-lg outline outline-1 -outline-offset-1 bg-n-alpha-1 text-n-slate-12 disabled:opacity-60 disabled:cursor-not-allowed"
      :class="
        isOpen ? 'outline-n-blue-9' : 'outline-n-weak hover:outline-n-slate-6'
      "
      @click="toggle"
    >
      <span class="truncate min-w-0" :class="{ 'text-n-slate-10': !selected }">
        {{ triggerLabel }}
      </span>
      <Icon
        icon="i-lucide-chevron-down"
        class="transition-transform shrink-0 size-4 text-n-slate-11"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <Teleport to="body" :disabled="inline">
      <ul
        v-if="isOpen"
        ref="menuRef"
        :style="inline ? undefined : menuStyle"
        class="flex flex-col gap-1 px-2 py-2 overflow-x-hidden overflow-y-auto text-sm list-none border shadow-md bg-n-solid-1 rounded-xl border-n-weak max-h-60"
        :class="inline ? 'relative w-full mt-1 z-20' : ''"
      >
        <li v-for="option in options" :key="String(option.value)">
          <button
            type="button"
            :disabled="option.disabled"
            class="flex items-center justify-between w-full gap-2 px-2.5 py-1.5 text-left rounded-lg text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :class="{ 'bg-n-alpha-2': sameValue(option.value, modelValue) }"
            @click="choose(option)"
          >
            <span class="whitespace-nowrap">{{ option.label }}</span>
            <Icon
              v-if="sameValue(option.value, modelValue)"
              icon="i-lucide-check"
              class="shrink-0 size-4 text-n-blue-11"
            />
          </button>
        </li>
      </ul>
    </Teleport>
  </div>
</template>
